/**
 * CreateSalePage - Mobile-friendly full-page sale creation
 *
 * This is a dedicated page for creating sales on mobile devices.
 * Desktop users use the modal; mobile users are redirected here.
 */

import { useState, useEffect, useCallback } from 'react'
import { toLocalDateString } from '../utils/dateUtils'
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom'
import {
  fetchCustomers,
  fetchCustomerBranches,
  fetchProducts,
  fetchProductTypes,
  fetchPriceLists,
  createSale,
  updateSale,
  createSaleItems,
  deleteSaleItemsBySaleId,
  findExistingPendingSale
} from '../supabase-client'
import { getTodayISOString } from '../utils/dateUtils.js'
import Header from './Header'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
// Native date input used for mobile OS integration
// Combobox replaced with full-screen picker for mobile
import { ArrowLeft, ChevronRight } from 'lucide-react'
import { cn } from '@/lib/utils'

const formatDate = (date) => {
  const dateObject = date instanceof Date ? date : new Date(date)
  return dateObject.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: '2-digit'
  })
}

// Format date for native HTML input (YYYY-MM-DD)
const toInputDateString = (date) => {
  const d = date instanceof Date ? date : new Date(date)
  return toLocalDateString(d)
}

export default function CreateSalePage({ user, onLogout }) {
  const navigate = useNavigate()
  const location = useLocation()
  const [searchParams] = useSearchParams()
  const editId = searchParams.get('edit') // For editing existing sales

  const [customers, setCustomers] = useState([])
  const [selectedCustomer, setSelectedCustomer] = useState('')
  const [selectedCustomerName, setSelectedCustomerName] = useState('')
  const [selectedBranch, setSelectedBranch] = useState('')
  const [customerBranches, setCustomerBranches] = useState([])
  const [products, setProducts] = useState([])
  const [productTypes, setProductTypes] = useState([])
  const [priceLists, setPriceLists] = useState([])
  const [selectedDate, setSelectedDate] = useState(() => new Date())
  const [saleItems, setSaleItems] = useState([])
  const [loading, setLoading] = useState(false)
  const [initialLoading, setInitialLoading] = useState(true)

  // Load initial data
  useEffect(() => {
    const loadInitialData = async () => {
      setInitialLoading(true)
      try {
        const [customersData, productsData, productTypesData] = await Promise.all([
          fetchCustomers(),
          fetchProducts(),
          fetchProductTypes()
        ])

        setCustomers(customersData)
        setProducts(productsData)
        setProductTypes(productTypesData)

        // Initialize sale items with products
        const defaultProductType = productTypesData.find(pt => pt.name === 'A Class') || productTypesData[0]
        const initialItems = productsData.map(product => ({
          id: product.id,
          product_id: product.id,
          productName: product.strain,
          strain: product.strain,
          product_type_id: defaultProductType?.id || null,
          quantity: 0,
          price: product.base_price_per_kg || 0,
          subtotal: 0
        }))
        setSaleItems(initialItems)
      } catch (error) {
        console.error('Error loading initial data:', error)
      } finally {
        setInitialLoading(false)
      }
    }

    loadInitialData()
  }, [])

  // Handle customer selection from MobileCustomerPicker navigation state
  useEffect(() => {
    if (location.state?.selectedCustomerId) {
      const { selectedCustomerId, selectedCustomerName: name, selectedCustomerType } = location.state
      setSelectedCustomer(selectedCustomerId)
      setSelectedCustomerName(name + (selectedCustomerType ? ` (${selectedCustomerType})` : ''))

      // Trigger branch loading and pricing
      handleCustomerChange(selectedCustomerId)

      // Clear the state so it doesn't re-trigger
      window.history.replaceState({}, document.title)
    }
  }, [location.state])

  const loadCustomerBranches = useCallback(async (customerId) => {
    if (!customerId) {
      setCustomerBranches([])
      setSelectedBranch('')
      return
    }

    try {
      const branches = await fetchCustomerBranches(customerId)
      setCustomerBranches(branches)

      if (branches.length === 1) {
        setSelectedBranch(branches[0].id)
      } else {
        setSelectedBranch('')
      }
    } catch (error) {
      console.error('Error loading customer branches:', error)
      setCustomerBranches([])
      setSelectedBranch('')
    }
  }, [])

  const updatePricingForCustomerType = useCallback((customerType) => {
    setSaleItems(prev => {
      return prev.map(item => {
        const product = products.find(p => p.id === item.product_id)
        const priceList = priceLists.find(pl =>
          pl.product_id === item.product_id &&
          pl.customer_type.toLowerCase() === customerType
        )
        const basePrice = priceList?.price || product?.base_price_per_kg || 0
        const productTypeData = productTypes.find(pt => pt.id === item.product_type_id)
        const priceMultiplier = productTypeData?.price_multiplier ?? 1.0
        const finalPrice = basePrice * priceMultiplier
        const subtotal = item.quantity * finalPrice

        return {
          ...item,
          price: finalPrice,
          subtotal: subtotal
        }
      })
    })
  }, [products, productTypes, priceLists])

  const loadPriceListsForCustomer = useCallback(async (customerType) => {
    try {
      const priceListData = await fetchPriceLists(customerType.toLowerCase())
      setPriceLists(priceListData)
      updatePricingForCustomerType(customerType.toLowerCase())
    } catch (error) {
      console.error('Error loading price lists:', error)
      setPriceLists([])
    }
  }, [updatePricingForCustomerType])

  const handleCustomerChange = useCallback(async (customerId) => {
    setSelectedCustomer(customerId)
    await loadCustomerBranches(customerId)

    if (customerId) {
      const customer = customers.find(c => c.id === customerId)
      if (customer) {
        const defaultBranch = customer.branches?.[0]
        const customerType = defaultBranch?.customer_type?.name
        if (customerType) {
          await loadPriceListsForCustomer(customerType)
        }
      }
    }
  }, [customers, loadCustomerBranches, loadPriceListsForCustomer])

  const handleQuantityChange = (productId, quantity) => {
    const numQuantity = parseFloat(quantity) || 0
    setSaleItems(prev => prev.map(item => {
      if (item.product_id === productId) {
        const subtotal = numQuantity * item.price
        return {
          ...item,
          quantity: numQuantity,
          subtotal: subtotal
        }
      }
      return item
    }))
  }

  const handleQuantityFocus = (event) => {
    if (event.target.value === '0') {
      event.target.value = ''
    }
  }

  const handleQuantityBlur = (event, productId) => {
    if (event.target.value === '' || event.target.value.trim() === '') {
      handleQuantityChange(productId, '0')
    }
  }

  const handleProductTypeChange = (productId, productTypeId) => {
    setSaleItems(prev => prev.map(item => {
      if (item.product_id === productId) {
        const customer = customers.find(c => c.id === selectedCustomer)
        const customerTypeRaw = customer?.type
        const customerType = customerTypeRaw ? customerTypeRaw.toLowerCase() : ''
        const priceList = priceLists.find(pl =>
          pl.product_id === productId &&
          pl.customer_type === customerType
        )

        const product = products.find(p => p.id === productId)
        const basePrice = priceList?.price || product?.base_price_per_kg || item.price || 0

        const selectedType = productTypes.find(pt => pt.id === productTypeId)
        const priceMultiplier = selectedType?.price_multiplier ?? 1.0
        const finalPrice = basePrice * priceMultiplier
        const subtotal = item.quantity * finalPrice

        return {
          ...item,
          product_type_id: productTypeId,
          price: finalPrice,
          subtotal: subtotal
        }
      }
      return item
    }))
  }

  const getTotalAmount = () => {
    return saleItems.reduce((sum, item) => sum + item.subtotal, 0)
  }

  const handleSave = async () => {
    if (!selectedCustomer) {
      alert('Please select a customer')
      return
    }

    if (customerBranches.length > 0 && !selectedBranch) {
      alert('Please select a branch')
      return
    }

    const itemsToSave = saleItems.filter(item => item.quantity > 0)

    if (itemsToSave.length === 0) {
      alert('Please add at least one item with quantity greater than 0')
      return
    }

    setLoading(true)

    try {
      let savedSale

      // Check for existing pending sale for same customer + branch + date
      const existingPending = await findExistingPendingSale(
        selectedCustomer,
        selectedBranch || null,
        selectedDate
      )

      if (existingPending) {
        // Append items to existing pending sale
        savedSale = existingPending
      } else {
        const saleData = {
          customer_id: selectedCustomer,
          branch_id: selectedBranch || null,
          date: selectedDate
        }

        savedSale = await createSale(saleData)
      }

      const saleItemsData = itemsToSave.map(item => ({
        sale_id: savedSale.id,
        product_id: item.product_id,
        product_type_id: item.product_type_id,
        quantity: item.quantity,
        price: item.price,
        discount_percent: null,
        discount_note: null
      }))

      await createSaleItems(saleItemsData)

      // Navigate back to sales list
      navigate('/sales')
    } catch (error) {
      console.error('Error saving sale:', error)
      alert('Failed to save sale. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  const handleCancel = () => {
    navigate('/sales')
  }

  if (initialLoading) {
    return (
      <div className="odin-page">
        <Header user={user} onLogout={onLogout} />
        <div className="flex items-center justify-center h-64">
          <div className="odin-spinner" />
        </div>
      </div>
    )
  }

  return (
    <div className="odin-page">
      <Header user={user} onLogout={onLogout} />

      <main className="px-4 py-4 pb-32">
        {/* Back button and title */}
        <div className="flex items-center gap-3 mb-4">
          <Button variant="ghost" size="sm" onClick={handleCancel} className="p-2">
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <h1 className="text-xl font-bold">Create Sale</h1>
        </div>

        {/* Form fields - stacked vertically */}
        <div className="space-y-4 mb-6">
          <div className="space-y-1.5">
            <Label className="text-sm font-medium">Customer</Label>
            <button
              type="button"
              onClick={() => navigate('/sales/new/select-customer', {
                state: { selectedCustomerId: selectedCustomer }
              })}
              disabled={loading}
              className="flex h-9 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background disabled:cursor-not-allowed disabled:opacity-50 odin-focus"
            >
              <span className={selectedCustomer ? 'text-foreground' : 'text-muted-foreground'}>
                {selectedCustomerName || 'Select Customer'}
              </span>
              <ChevronRight className="h-4 w-4 text-muted-foreground" />
            </button>
          </div>

          <div className="space-y-1.5">
            <Label className="text-sm font-medium">Branch</Label>
            <Select
              value={selectedBranch}
              onValueChange={setSelectedBranch}
              disabled={!selectedCustomer || customerBranches.length === 0 || loading}
            >
              <SelectTrigger className="w-full">
                <SelectValue
                  placeholder={
                    !selectedCustomer
                      ? 'Select customer first'
                      : customerBranches.length === 0
                        ? 'No branches'
                        : 'Select Branch'
                  }
                />
              </SelectTrigger>
              <SelectContent>
                {customerBranches.map(branch => (
                  <SelectItem key={branch.id} value={branch.id}>
                    {branch.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5">
            <Label className="text-sm font-medium">Date</Label>
            <input
              type="date"
              value={toInputDateString(selectedDate)}
              onChange={(e) => setSelectedDate(new Date(e.target.value + 'T00:00:00'))}
              className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground disabled:cursor-not-allowed disabled:opacity-50 odin-focus appearance-none"
            />
          </div>
        </div>

        {/* Product list with dividers */}
        <div className="border rounded-lg bg-white overflow-hidden">
          <div className="px-3 py-2 bg-muted/50 border-b">
            <Label className="text-sm font-medium text-muted-foreground">Products</Label>
          </div>
          <div className="divide-y">
            {saleItems.map(item => (
              <div
                key={item.product_id}
                className={cn(
                  "px-3 py-2.5",
                  item.quantity > 0 && 'bg-green-50'
                )}
              >
                <div className="flex items-center justify-between gap-2 mb-1.5">
                  <span className="font-medium text-sm flex-1">{item.productName}</span>
                  <Select
                    value={item.product_type_id}
                    onValueChange={(value) => handleProductTypeChange(item.product_id, value)}
                  >
                    <SelectTrigger className="w-20 h-7 text-xs">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {productTypes.map(type => (
                        <SelectItem key={type.id} value={type.id}>
                          {type.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2">
                    <Input
                      type="number"
                      min="0"
                      step="0.1"
                      value={item.quantity}
                      onChange={(e) => handleQuantityChange(item.product_id, e.target.value)}
                      onFocus={handleQuantityFocus}
                      onBlur={(e) => handleQuantityBlur(e, item.product_id)}
                      placeholder="0"
                      className="w-16 h-9 text-center"
                      inputMode="decimal"
                    />
                    <span className="text-xs text-muted-foreground">kg @ £{item.price.toFixed(2)}</span>
                  </div>
                  <span className={cn(
                    "font-semibold text-sm",
                    item.subtotal > 0 ? "text-green-700" : "text-muted-foreground"
                  )}>
                    £{item.subtotal.toFixed(2)}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </main>

      {/* Fixed footer with total and buttons - uses safe-area-inset for mobile browser chrome */}
      <div
        className="fixed bottom-0 left-0 right-0 bg-white border-t shadow-lg p-4 space-y-3"
        style={{ paddingBottom: 'calc(1rem + env(safe-area-inset-bottom, 0px))' }}
      >
        <div className="flex items-center justify-between">
          <span className="text-muted-foreground">Total</span>
          <span className="text-2xl font-bold text-green-700">£{getTotalAmount().toFixed(2)}</span>
        </div>
        <div className="flex gap-3">
          <Button variant="outline" onClick={handleCancel} disabled={loading} className="flex-1">
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={loading} className="flex-1 bg-green-700 hover:bg-green-800">
            {loading ? 'Saving...' : 'Create Sale'}
          </Button>
        </div>
      </div>
    </div>
  )
}
